// Reusable right-side detail Drawer — the chosen DETAIL-VIEW pattern across every
// entity list (networks, clients, service-definitions, publications, tokens,
// users, tasks, objects). A list row click opens it; it slides in from the right
// over a dimmed backdrop and closes on Escape / backdrop click / the ✕ button.
// `actions` render in the header (entity CRUD + entity-specific actions).

import { useEffect, type ReactNode } from 'react';

export function Drawer({
  title,
  subtitle,
  actions,
  onClose,
  children,
  width,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="drawer-backdrop" onClick={onClose} role="presentation">
      <aside
        className="drawer"
        style={width ? { width } : undefined}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="drawer-head">
          <div className="drawer-titles">
            <h2>{title}</h2>
            {subtitle && <div className="drawer-sub">{subtitle}</div>}
          </div>
          <button className="btn btn-sm drawer-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        {actions && <div className="drawer-actions">{actions}</div>}
        <div className="drawer-body">{children}</div>
      </aside>
    </div>
  );
}

// A labeled key/value row for drawer detail bodies.
export function DetailRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="detail-row">
      <span className="detail-label">{label}</span>
      <span className="detail-value">{children}</span>
    </div>
  );
}
